/** Parse a patients table JSON/text field (same rules as ICD-10 validator). */
export function parsePatientJsonField(value) {
  if (value === null || value === undefined) return null;
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === "null") return null;

  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
}

function countTruthyEntries(items) {
  if (!Array.isArray(items)) return 0;
  return items.filter((item) => {
    if (item == null) return false;
    if (typeof item === "string") return item.trim().length > 0;
    if (typeof item === "object") return Object.keys(item).length > 0;
    return true;
  }).length;
}

/** @returns {number} */
export function getDotPhrasesCount(value) {
  const data = parsePatientJsonField(value);
  if (data === null || data === undefined) return 0;

  if (typeof data === "string") {
    return data.trim() ? 1 : 0;
  }

  if (Array.isArray(data)) {
    return countTruthyEntries(data);
  }

  if (typeof data === "object") {
    const nested =
      data.macros_applied ??
      data.applied_macros ??
      data.macros ??
      data.dot_phrases ??
      data.selected_macros;

    if (Array.isArray(nested)) {
      return countTruthyEntries(nested);
    }

    if (typeof nested === "string") {
      const parts = nested
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
      return parts.length;
    }

    return Object.keys(data).length > 0 ? Object.keys(data).length : 0;
  }

  return 0;
}

export function isDotPhrasesEmpty(value) {
  return getDotPhrasesCount(value) === 0;
}

/** @returns {string|null} */
export function getEandMCode(value) {
  const data = parsePatientJsonField(value);
  if (data === null || data === undefined) return null;

  if (typeof data === "string") {
    const trimmed = data.trim();
    return trimmed || null;
  }

  if (typeof data === "number" || typeof data === "boolean") {
    return String(data);
  }

  if (typeof data === "object") {
    const code =
      data.e_and_m_code ??
      data.e_and_m ??
      data.code ??
      data.em_code ??
      data.cpt_code;

    if (code != null && String(code).trim()) {
      return String(code).trim();
    }

    const firstNonEmptyValue = Object.values(data).find(
      (entry) => entry != null && String(entry).trim()
    );
    if (firstNonEmptyValue != null) {
      return String(firstNonEmptyValue).trim();
    }

    return null;
  }

  return null;
}

export function isEandMEmpty(value) {
  return getEandMCode(value) == null;
}

export function getPatientSummaryMetrics(value) {
  const data = parsePatientJsonField(value);
  if (data === null || data === undefined) {
    return { present: false, label: null };
  }

  if (typeof data === "string") {
    const trimmed = data.trim();
    if (!trimmed) return { present: false, label: null };
    return {
      present: true,
      label: `${trimmed.length} character${trimmed.length === 1 ? "" : "s"}`,
    };
  }

  if (Array.isArray(data)) {
    if (data.length === 0) return { present: false, label: null };
    return {
      present: true,
      label: `${data.length} item${data.length === 1 ? "" : "s"}`,
    };
  }

  if (typeof data === "object") {
    if (Object.keys(data).length === 0) {
      return { present: false, label: null };
    }

    const textFields = ["summary", "text", "content", "patient_summary", "value"];
    for (const key of textFields) {
      const text = data[key];
      if (typeof text === "string" && text.trim()) {
        const len = text.trim().length;
        return {
          present: true,
          label: `${len} character${len === 1 ? "" : "s"}`,
        };
      }
    }

    const fieldCount = Object.keys(data).length;
    return {
      present: true,
      label: `${fieldCount} field${fieldCount === 1 ? "" : "s"}`,
    };
  }

  return { present: true, label: "present" };
}

export function isPatientSummaryEmpty(value) {
  return !getPatientSummaryMetrics(value).present;
}
